import React from "react";
import styles from "./styles.modules.scss";
import classNames from "classnames";

const random = (min: number, max: number) =>
    Math.floor(Math.random() * (max - min)) + min;

const useRandomInterval = (
    callback: () => void,
    minDelay: number,
    maxDelay: number,
) => {
    const timeoutId = React.useRef<number>();
    const savedCallback = React.useRef(callback);

    React.useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    React.useEffect(() => {
        const isEnabled =
            typeof minDelay === "number" && typeof maxDelay === "number";

        if (isEnabled) {
            const handleTick = () => {
                const nextTickAt = random(minDelay, maxDelay);
                timeoutId.current = window.setTimeout(() => {
                    savedCallback.current();
                    handleTick();
                }, nextTickAt);
            };
            handleTick();
        }

        return () => window.clearTimeout(timeoutId.current);
    }, [minDelay, maxDelay]);

    const cancel = React.useCallback(function () {
        window.clearTimeout(timeoutId.current);
    }, []);

    return cancel;
};

const generateSparkle = (
    color: string,
    sparkleSize: { min: number; max: number },
    sparkleArea: {
        top: number;
        left: number;
        right: number;
        bottom: number;
    },
) => {
    const sparkle = {
        id: String(random(10000, 99999)),
        createdAt: Date.now(),
        color,
        size: random(sparkleSize.min, sparkleSize.max),
        style: {
            top: random(sparkleArea.top, sparkleArea.bottom) + "%",
            left: random(sparkleArea.left, sparkleArea.right) + "%",
        },
    };
    return sparkle;
};

export const Sparkles = ({
    color,
    children,
    sparkleSize = {
        min: 10,
        max: 20,
    },

    sparkleArea = {
        top: 0,
        left: 0,
        right: 100,
        bottom: 100,
    },
    sparkleAmount = 2,
    active = true,
}: {
    color: string;
    children: JSX.Element | JSX.Element[];
    sparkleAmount?: number;
    sparkleSize?: {
        min: number;
        max: number;
    };
    sparkleArea?: {
        top: number;
        left: number;
        right: number;
        bottom: number;
    };
    active?: boolean;
}) => {
    const [sparkles, setSparkles] = React.useState(() => {
        return Array.from({ length: sparkleAmount }, () =>
            generateSparkle(color, sparkleSize, sparkleArea),
        );
    });

    useRandomInterval(
        () => {
            const sparkle = generateSparkle(color, sparkleSize, sparkleArea);
            const now = Date.now();

            const nextSparkles = sparkles.filter((sp) => {
                const delta = now - sp.createdAt;

                return delta < 750;
            });

            nextSparkles.push(sparkle);
            setSparkles(nextSparkles);
        },
        500,
        2000,
    );

    const activeSparkles = active ? sparkles : [];

    return (
        <div className={`${styles.sparkles} sparkles`}>
            {activeSparkles.map((sparkle) => (
                <Sparkle
                    key={sparkle.id}
                    color={sparkle.color}
                    size={sparkle.size}
                    style={sparkle.style}
                />
            ))}

            <div className={`${styles.childrenWrapper} sparkles-children`}>
                {children}
            </div>
        </div>
    );
};

const Sparkle = ({
    size,
    color,
    style,
}: {
    size: number;
    color: string;
    style: Record<string, string>;
}) => {
    const path =
        "M26.5 25.5C19.0043 33.3697 0 34 0 34C0 34 19.1013 35.3684 26.5 43.5C33.234 50.901 34 68 34 68C34 68 36.9884 50.7065 44.5 43.5C51.6431 36.647 68 34 68 34C68 34 51.6947 32.0939 44.5 25.5C36.5605 18.2235 34 0 34 0C34 0 33.6591 17.9837 26.5 25.5Z";
    return (
        <div
            className={classNames("sparkle-wrapper", styles.sparkleWrapper)}
            style={style}
        >
            <svg
                className={styles.sparkleSvg}
                width={size}
                height={size}
                viewBox="0 0 68 68"
                fill="none"
            >
                <path d={path} fill={color} />
            </svg>
        </div>
    );
};
